import { TransactionDetail } from "../types/history";
import { createColumnHelper, FilterFn } from "@tanstack/react-table";
import { TimestampCell } from "../components/TableContainer/elements/TimestampCell";
import { NetworkCell } from "components/TableContainer/elements/NetworkCell";
import { GlobalStore } from "../store/global/GlobalStore";
import { BuyTokensOrSellTokensCell } from "../components/TableContainer/elements/BuyTokensOrSellTokensCell";
import { Amount } from "../types/amount";
import { TotalAmountTradedCell } from "../components/TableContainer/elements/TotalAmountTradedCell";
import { ReactComponent as ArrowIcon } from "../img/trade-history/PointedRight.svg";
import { useIsTradeHistoryReturned } from "./useIsTradeHistoryReturned";
import { Filter, FiltersInterface } from "config/consts";
import { OpenInExplorerCell } from "../components/TableContainer/elements/OpenInExplorerCell";

declare module "@tanstack/table-core" {
  interface FilterFns {
    network: FilterFn<FiltersInterface["network"]>;
    transaction: FilterFn<FiltersInterface["transaction"]>;
    token: FilterFn<FiltersInterface["token"]>;
    amount: FilterFn<FiltersInterface["amount"]>;
  }
}

export const useActivityColumnDefinitions = () => {
  const theme = GlobalStore.useState(s => s.theme);
  const isTradeHistoryReturned = useIsTradeHistoryReturned();
  /**
   * Columns
   * 1. Arrows
   * 2. Trade Type + Network + Date
   * 3.Tokens out
   * 4. Tokens in
   * 5. Details arrow
   */
  const columnHelper = createColumnHelper<TransactionDetail>();

  return [
    columnHelper.accessor("timestamp", {
      id: "timestamp",
      cell: props => <TimestampCell timestamp={props.getValue()} />,
      header: () => "Timestamp",
      maxSize: 10,
    }),
    columnHelper.accessor("chain_id", {
      id: "network",
      cell: props => <NetworkCell chainId={props.getValue()} />,
      header: () => "Network",
      minSize: 5,
      size: 5,
      filterFn: "network",
    }),
    // Tokens out
    columnHelper.accessor("sellTokens", {
      id: "sellTokens",
      header: "Sell Tokens",
      maxSize: 30,
      minSize: 30,
      cell: info => {
        return (
          <BuyTokensOrSellTokensCell tokenHistoryInfo={info.getValue()} columnId={"sellTokens"} />
        );
      },
      filterFn: "token",
    }),
    columnHelper.accessor(() => undefined, {
      id: "arrow",
      header: "Arrow Icon",
      size: 5,
      maxSize: 5,
      cell: () => <ArrowIcon color={isTradeHistoryReturned ? "inherit" : theme.theme[600]} />,
    }),
    columnHelper.accessor("buyTokens", {
      id: "buyTokens",
      maxSize: 30,
      minSize: 30,
      header: "Buy Tokens",
      cell: info => {
        return (
          <BuyTokensOrSellTokensCell tokenHistoryInfo={info.getValue()} columnId={"buyTokens"} />
        );
      },
      filterFn: "token",
    }),
    // Tokens In
    columnHelper.accessor("volumeUsd", {
      id: "volumeUsd",
      header: "Volume",
      size: 20,
      maxSize: 20,
      cell: info => {
        const formattedUsd = new Amount(info.getValue().toString()).format(2);
        return <TotalAmountTradedCell totalAmountTraded={formattedUsd} />;
      },
      filterFn: "amount",
    }),
    columnHelper.accessor("txHash", {
      id: "txHash",
      header: "txHash",
      size: 5,
      minSize: 0,
      cell: info => {
        return isTradeHistoryReturned ? (
          // TODO: Allow click on explorer cell to override opening Transaction Detail drawer
          <OpenInExplorerCell chainId={info.row.getValue("network")} txHash={info.getValue()} />
        ) : null;
      },
    }),

    // Hidden columns (used for filtering)
    columnHelper.accessor(value => ({ sender: value.sender, receiver: value.receiver }), {
      id: "transaction",
      filterFn: "transaction",
      cell: () => null,
      size: 0,
      maxSize: 0,
      enableHiding: true,
    }),
    columnHelper.accessor(
      value => Object.keys(value.buyTokens).concat(Object.keys(value.sellTokens)),
      {
        id: "token",
        cell: () => null,
        size: 0,
        maxSize: 0,
        filterFn: "token",
      }
    ),
  ];
};

// Helpers

export const MAP_FILTER_TO_COL_ID: Record<Filter, string> = {
  [Filter.Network]: "network",
  [Filter.Transaction]: "transaction",
  [Filter.Token]: "token",
  [Filter.Amount]: "volumeUsd",
  [Filter.Date]: "timestamp",
};
